import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  getAdminPeakPrices,
  postAdminPeakPrice,
  deleteAdminPeakPrice,
} from 'utils/api/admin';

const SPC_OPTIONS = [
  { value: '', label: '전체 (단지 기본값)' },
  { value: '25pt', label: '25평형' },
  { value: '32pt', label: '32평형' },
  { value: '40pt', label: '40평형' },
];

const emptyForm = {
  hscp_no: '',
  dong_code: '',
  spc_type_cd: '',
  peakPrice: '',
  peakYearMonth: '',
  memo: '',
};

const PeakPrices = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const size = 20;
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchList = async (searchValue) => {
    try {
      const res = await getAdminPeakPrices({
        search: searchValue !== undefined ? searchValue : search,
        page,
        size,
      });
      setItems(res.results.items);
      setTotal(res.results.total);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchList(search);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      hscp_no: item.hscp_no,
      dong_code: item.dong_code,
      spc_type_cd: item.spc_type_cd || '',
      peakPrice: String(item.peakPrice || ''),
      peakYearMonth: item.peakYearMonth || '',
      memo: item.memo || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await postAdminPeakPrice({
        hscp_no: form.hscp_no.trim(),
        dong_code: form.dong_code.trim(),
        spc_type_cd: form.spc_type_cd || undefined,
        peakPrice: Number(form.peakPrice),
        peakYearMonth: form.peakYearMonth || undefined,
        memo: form.memo || undefined,
      });
      setShowModal(false);
      setForm(emptyForm);
      fetchList();
    } catch (err) {
      alert(err?.response?.data?.message || '저장에 실패했습니다.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('해당 전고점 설정을 삭제하시겠습니까?')) return;
    try {
      await deleteAdminPeakPrice(id);
      fetchList();
    } catch (err) {
      alert(err?.response?.data?.message || '삭제에 실패했습니다.');
    }
  };

  const totalPages = Math.ceil(total / size);
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('ko-KR') : '-');
  const formatPrice = (v) => (v ? `${Number(v).toLocaleString()}만원` : '-');

  return (
    <Container>
      <Header>
        <Title>전고점 수동 설정</Title>
        <AddButton onClick={openCreate}>+ 전고점 입력</AddButton>
      </Header>
      <Desc>
        전고점이 집계되지 않은 단지에 대해 관리자가 실거래최고가를 직접
        지정합니다.
      </Desc>
      <SearchForm onSubmit={handleSearch}>
        <SearchInput
          placeholder="단지코드 또는 법정동코드 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <SearchButton type="submit">검색</SearchButton>
      </SearchForm>
      <Info>총 {total}건</Info>
      <Table>
        <thead>
          <tr>
            <Th>단지코드</Th>
            <Th>법정동</Th>
            <Th>평형</Th>
            <Th>전고점</Th>
            <Th>거래년월</Th>
            <Th>메모</Th>
            <Th>수정일</Th>
            <Th>관리</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it._id}>
              <Td>{it.hscp_no}</Td>
              <Td>{it.dong_code}</Td>
              <Td>{it.spc_type_cd || '전체'}</Td>
              <Td>{formatPrice(it.peakPrice)}</Td>
              <Td>{it.peakYearMonth || '-'}</Td>
              <Td style={{ fontSize: '12px', color: '#8f9bba' }}>
                {it.memo || '-'}
              </Td>
              <Td>{formatDate(it.updatedAt)}</Td>
              <Td>
                <Actions>
                  <EditBtn onClick={() => openEdit(it)}>수정</EditBtn>
                  <DeleteBtn onClick={() => handleDelete(it._id)}>
                    삭제
                  </DeleteBtn>
                </Actions>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page <= 1} onClick={() => setPage(page - 1)}>
            이전
          </PageBtn>
          <PageInfo>
            {page} / {totalPages}
          </PageInfo>
          <PageBtn
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </PageBtn>
        </Pagination>
      )}

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>전고점 입력/수정</ModalTitle>
            <Form onSubmit={handleSubmit}>
              <Label>단지코드 (hscp_no)</Label>
              <Input
                required
                value={form.hscp_no}
                onChange={(e) => setForm({ ...form, hscp_no: e.target.value })}
              />
              <Label>법정동 코드 (dong_code)</Label>
              <Input
                required
                value={form.dong_code}
                onChange={(e) =>
                  setForm({ ...form, dong_code: e.target.value })
                }
              />
              <Label>평형 타입</Label>
              <Select
                value={form.spc_type_cd}
                onChange={(e) =>
                  setForm({ ...form, spc_type_cd: e.target.value })
                }
              >
                {SPC_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              <Label>전고점 (만원)</Label>
              <Input
                type="number"
                min="1"
                required
                placeholder="예: 120000"
                value={form.peakPrice}
                onChange={(e) =>
                  setForm({ ...form, peakPrice: e.target.value })
                }
              />
              <Label>거래년월 (YYYYMM) — 선택</Label>
              <Input
                placeholder="예: 202108"
                value={form.peakYearMonth}
                onChange={(e) =>
                  setForm({ ...form, peakYearMonth: e.target.value })
                }
              />
              <Label>메모 — 선택</Label>
              <Input
                value={form.memo}
                onChange={(e) => setForm({ ...form, memo: e.target.value })}
              />
              <ModalActions>
                <CancelBtn type="button" onClick={() => setShowModal(false)}>
                  취소
                </CancelBtn>
                <SubmitBtn type="submit">저장</SubmitBtn>
              </ModalActions>
            </Form>
          </ModalBox>
        </Overlay>
      )}
    </Container>
  );
};

const Container = styled.div``;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;
const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #232d42;
  margin: 0;
`;
const Desc = styled.p`
  font-size: 13px;
  color: #8f9bba;
  margin: 0 0 16px 0;
`;
const AddButton = styled.button`
  padding: 8px 20px;
  background: #644ded;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #5338d4;
  }
`;
const SearchForm = styled.form`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;
const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  width: 300px;
  outline: none;
  &:focus {
    border-color: #644ded;
  }
`;
const SearchButton = styled.button`
  padding: 8px 20px;
  background: #644ded;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
`;
const Info = styled.div`
  font-size: 13px;
  color: #8f9bba;
  margin-bottom: 12px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;
const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #8f9bba;
  border-bottom: 1px solid #e4e4e4;
  background: #fafafa;
`;
const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #232d42;
  border-bottom: 1px solid #f0f0f0;
`;
const Actions = styled.div`
  display: flex;
  gap: 6px;
`;
const EditBtn = styled.button`
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #644ded;
  background: #fff;
  color: #644ded;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: #644ded;
    color: #fff;
  }
`;
const DeleteBtn = styled.button`
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #dc3545;
  background: #fff;
  color: #dc3545;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: #dc3545;
    color: #fff;
  }
`;
const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
`;
const PageBtn = styled.button`
  padding: 6px 16px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;
const PageInfo = styled.span`
  font-size: 13px;
  color: #8f9bba;
`;
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const ModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  width: 440px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
`;
const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #232d42;
  margin: 0 0 20px 0;
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #8f9bba;
  margin-top: 4px;
`;
const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #644ded;
  }
`;
const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #644ded;
  }
`;
const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;
const CancelBtn = styled.button`
  padding: 10px 24px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
`;
const SubmitBtn = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  background: #644ded;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #5338d4;
  }
`;

export default PeakPrices;
